// The single file of a quote (docs/servicios/cotizaciones.md › «Ficha única»): one record, edited until
// the client accepts it. Pure rules shared by publish-proposal.mts, quote-accept.mts, the person's space
// and the screens: the totals, whether what the client has differs from the record, the history of sends
// and what the file is called. Sending again updates the same document and link; each send keeps only its
// date and totals (never a copy of a draft). The accepted document is the definitive one and is frozen.
import { isAccepted, wasAccepted, isReplaced } from './quoteRules.js';
import { acceptanceMarksOf } from './pipelineFacts.js';
import { classifyRecipient } from './personQuotes.js';
const classifyKind = (f) => classifyRecipient(f).kind;

const name = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
const num = (v) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
const round2 = (n) => Math.round(n * 100) / 100;

/** The lines of a record (Quote Lines JSON), only the ones with a service. */
export function linesOf(f = {}) {
  let list = [];
  try { list = JSON.parse(f['Quote Lines JSON'] || '[]'); } catch { list = []; }
  return (Array.isArray(list) ? list : []).filter(l => String(l?.service || '').trim());
}

/** One-time and monthly totals of some lines. */
export function totalsOfLines(lines = []) {
  const once = round2(lines.reduce((s, l) => s + (num(l.onceFee) || 0), 0));
  const monthly = round2(lines.reduce((s, l) => s + (num(l.monthlyFee) || 0), 0));
  return { once, monthly };
}

/**
 * What the client sees, reduced to what matters for an acceptance: the lines (service, scope, fees, note,
 * in order), the currency and the conditions, as a fingerprint (the key never carries the content). Two
 * contents are the same offer only if this key is equal.
 */
export function contentKey({ lines = [], currency = '', conditions = '' } = {}) {
  const clean = lines.filter(l => String(l?.service || '').trim()).map(l => [
    String(l.service || '').trim(), String(l.scope || '').trim(), num(l.onceFee), num(l.monthlyFee), String(l.note || '').trim(),
  ]);
  return fingerprint(JSON.stringify([clean, String(currency || '').trim(), String(conditions || '').trim()]));
}
/** A short fingerprint of a text (two 32-bit FNV-1a passes): it tells two contents apart without carrying them. */
function fingerprint(text) {
  let a = 0x811c9dc5, b = 0x01000193 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c ^ (i & 0xff), 0x01000193 + 2) >>> 0;
  }
  return `k1-${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}
export const recordContent = (f = {}) => ({ lines: linesOf(f), currency: f['Quote Currency'] || 'USD', conditions: f['Quote Conditions'] || '' });
export const recordContentKey = (f = {}) => contentKey(recordContent(f));

/** The snapshot of a published document (Landings › Proposal Snapshot JSON), or null. */
export function snapshotOf(landing) {
  try { const s = JSON.parse(landing?.fields?.['Proposal Snapshot JSON'] || 'null'); return s && typeof s === 'object' ? s : null; } catch { return null; }
}
export const snapshotContentKey = (s) => (s ? contentKey({ lines: s.lines || [], currency: s.currency || '', conditions: s.conditions || '' }) : '');

/** The record has changes the client has not received yet (only meaningful while not accepted). */
export const hasUnpublishedChanges = (f, snapshot) => !!snapshot && snapshotContentKey(snapshot) !== recordContentKey(f);

/**
 * The sends of a document: its date and totals each time it was published. A document published before
 * this history existed counts as one send, from its own date and content.
 */
export function sendsOf(snapshot, landing = null) {
  if (!snapshot) return [];
  if (Array.isArray(snapshot.sends) && snapshot.sends.length) return snapshot.sends.filter(s => s && s.at);
  const at = landing?.fields?.['Published At'] || snapshot.snapshotAt;
  return at ? [{ at, currency: snapshot.currency || '', ...totalsOfLines(snapshot.lines || []), legacy: true }] : [];
}

/** The next snapshot of a send: the content now, the earlier sends kept, this one added. */
export function nextSnapshot(previous, { at, content, accountName, recipient }) {
  const sends = sendsOf(previous);
  const totals = totalsOfLines(content.lines);
  return {
    snapshotAt: at,
    firstPublishedAt: previous?.firstPublishedAt || sends[0]?.at || at,
    currency: content.currency, lines: content.lines, conditions: content.conditions, accountName,
    ...(recipient ? { recipient } : {}),
    contentKey: contentKey(content),
    sends: [...sends.map(({ legacy, ...s }) => s), { at, currency: content.currency, ...totals }],
  };
}

/** The acceptance kept with the document (who, when, how): the document is definitive from then on. */
export const acceptanceOf = (snapshot) => (snapshot?.acceptance && snapshot.acceptance.at ? snapshot.acceptance : null);

export const ACCEPTED_VIA = { client_space: 'Desde su espacio', registered: 'Registrada por Ale' };

/**
 * What the file is called: «Cotización» until the client accepts it, «Contratación» after (also when the
 * client later cancelled it). An acceptance registered by mistake is a quote again (still locked).
 */
export function fileKindOf(f = {}) {
  const marks = acceptanceMarksOf(f['Pipeline Facts Note']);
  if (isAccepted(f)) return { kind: 'contract', label: 'Contratación', state: 'Aceptada' };
  if (marks.cancelled) return { kind: 'contract', label: 'Contratación', state: 'Cancelada por el cliente' };
  if (isReplaced(f)) return { kind: 'earlier', label: 'Versión anterior', state: 'Historial' };
  if (wasAccepted(f)) return { kind: 'quote', label: 'Cotización', state: 'Aceptación corregida' };
  if (name(f['Quote Status']) === 'Lost') return { kind: 'quote', label: 'Cotización', state: declineOf(f['Pipeline Facts Note']) ? 'No aceptada por el cliente' : 'Perdida' };
  const sent = Array.isArray(f['Consulting Landing']) && f['Consulting Landing'].length > 0;
  return { kind: 'quote', label: 'Cotización', state: sent ? 'Enviada' : 'En preparación' };
}

/** Why the record cannot be sent (published) now, or null. */
export function sendProblem(f = {}) {
  if (wasAccepted(f)) return isAccepted(f) ? 'quote_accepted' : 'quote_was_accepted';
  if (isReplaced(f)) return 'quote_replaced';
  if (name(f['Quote Status']) === 'Lost') return 'quote_lost';
  if (!linesOf(f).length) return 'quote_no_lines';
  return null;
}

/**
 * Why an acceptance cannot be registered now, or null. `snapshot` is what the client has (null if never
 * sent). `seenKey` (from the client's space): the content the client was looking at; if the record changed
 * since, she reviews it again before confirming.
 */
export function acceptProblem(f = {}, snapshot = null, { seenKey = null, requireSent = false } = {}) {
  if (isAccepted(f)) return 'already_accepted';
  if (wasAccepted(f)) return 'quote_was_accepted';
  if (isReplaced(f)) return 'quote_replaced';
  if (name(f['Quote Status']) === 'Lost') return 'quote_lost';
  if (!linesOf(f).length) return 'quote_no_lines';
  if (requireSent && !snapshot) return 'not_sent';
  if (snapshot && hasUnpublishedChanges(f, snapshot)) return 'unpublished_changes';
  if (seenKey !== null && seenKey !== recordContentKey(f)) return 'conditions_changed';
  return null;
}

// ── The summary of a file (docs/servicios/cotizaciones.md › «Cotizaciones y pagos») ──
// Agreed, received and pending are computed from the charges and payments (never stored). The balance keeps
// apart what already has dates (installments) from what still has none: before the plan exists the balance is
// the agreed total, never zero. Monthly amounts are shown apart (they have no agreed duration).
const centsOf = (n) => Math.round(Number(n || 0) * 100);
const fromCents = (c) => c / 100;
const sel2 = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';

/** The next open installment with something pending among some charges (with computed `state`), or null. */
export function nextDueOf(charges = []) {
  const open = charges.filter(c => sel2(c.fields?.['Charge Status']) === 'open' && (c.state?.pending || 0) > 0 && c.fields?.['Due On'])
    .sort((a, b) => String(a.fields['Due On']).localeCompare(String(b.fields['Due On'])));
  return open.length ? { dueOn: open[0].fields['Due On'], amount: open[0].state.pending, currency: open[0].state.currency } : null;
}

/**
 * The summary of a quote's file. `proposal`: proposalOfVersion of its accepted version (null if not accepted or
 * without access to payments); `charges`: the charges of that version (with `state`); `hasBilling`: whether the
 * payments could be read (otherwise received and balance are unknown, never zero).
 */
export function quoteSummary(f = {}, { proposal = null, charges = [], hasBilling = false } = {}) {
  const k = fileKindOf(f);
  const totals = totalsOfLines(linesOf(f));
  const currency = f['Quote Currency'] || 'USD';
  const status = name(f['Quote Status']);
  const stage = k.kind === 'contract' ? (k.state === 'Aceptada' ? 'accepted' : 'cancelled')
    : status === 'Lost' ? 'lost' : (Array.isArray(f['Consulting Landing']) && f['Consulting Landing'].length) ? 'sent' : 'draft';
  const out = { stage, currency, total: totals, agreed: totals.once, received: null, balance: null, scheduled: null, unscheduled: null, next: null, unresolved: false };
  if (stage !== 'accepted' || !hasBilling || !proposal) return out;
  const receivedC = centsOf(proposal.collected) + centsOf(proposal.earlierCollected);
  const scheduledC = centsOf(proposal.pending) + centsOf(proposal.keptPending);
  const unscheduledC = centsOf(proposal.notPlanned);
  return {
    ...out, agreed: proposal.agreed, received: fromCents(receivedC), scheduled: fromCents(scheduledC), unscheduled: fromCents(unscheduledC),
    balance: fromCents(scheduledC + unscheduledC), next: nextDueOf(charges), unresolved: !proposal.canPlan,
  };
}

/** The summary of charges without a quote (one plan or one charge). */
export function directSummary(charges = []) {
  const open = charges.filter(c => sel2(c.fields?.['Charge Status']) !== 'cancelled');
  const currency = open[0]?.state?.currency || open[0]?.fields?.['Currency'] || '';
  const sum = (k) => fromCents(open.reduce((s, c) => s + centsOf(c.state?.[k]), 0));
  const balance = sum('pending');
  return { stage: 'direct', currency, total: { once: sum('amount'), monthly: 0 }, agreed: sum('amount'), received: sum('collected'), balance, scheduled: balance, unscheduled: 0, next: nextDueOf(open), unresolved: false };
}

// ── «Completar datos del cliente» (quote-recipient.mts) ──
export const RECIPIENT_MARK = '[destinatario completado:';
/**
 * The fields to write when the administration completes the recipient of a quote Oike cannot classify. Never the
 * acceptance, the document, the lines or payments; a contact is written only if the record has none; a trace line
 * goes on top of Pipeline Facts Note (its earlier lines, acceptance marks included, stay). Returns { fields } or { error }.
 */
export function recipientCompletion(f = {}, { recipient, stakeholderId = '', accountId = '', by = '', now = new Date() } = {}) {
  const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
  if (classifyKind(f) !== 'review') return { error: 'not_review' };
  if (recipient === 'company') {
    // As a company: it was the administration's only («Por revisar») and stays so in the same write (Admin Only);
    // the company is linked only if the record had none, never changed
    const accounts = links(f['Account']);
    if (accounts.length > 1) return { error: 'several_companies' };
    if (accounts.length && accountId && accounts[0] !== accountId) return { error: 'company_fixed' };
    const account = accounts[0] || accountId;
    if (!/^rec[A-Za-z0-9]{14}$/.test(account)) return { error: 'company_required' };
    const stamp = now.toISOString().slice(0, 16).replace('T', ' ');
    const line = `Cliente completado ${RECIPIENT_MARK} empresa] (${stamp} UTC, ${String(by).slice(0, 120)}): antes sin destinatario claro; ahora contrata la empresa (${account}) y sigue siendo solo de la administración (Admin Only). No cambian la aceptación, el documento, las líneas ni los pagos.`;
    const fields = { 'Quote Recipient': 'company', 'Admin Only': true, 'Pipeline Facts Note': [line, f['Pipeline Facts Note'] || ''].filter(Boolean).join('\n') };
    if (!accounts.length) fields['Account'] = [account];
    return { fields, restrict: true };
  }
  if (recipient !== 'person') return { error: 'invalid_recipient' };
  const current = links(f['Stakeholder']);
  if (current.length > 1) return { error: 'several_contacts' };
  if (current.length && stakeholderId && current[0] !== stakeholderId) return { error: 'contact_fixed' };
  const contact = current[0] || stakeholderId;
  if (!/^rec[A-Za-z0-9]{14}$/.test(contact)) return { error: 'contact_required' };
  const stamp = now.toISOString().slice(0, 16).replace('T', ' ');
  const line = `Cliente completado ${RECIPIENT_MARK} persona] (${stamp} UTC, ${String(by).slice(0, 120)}): antes sin destinatario claro; ahora contrata la persona (contacto ${contact})${links(f['Account']).length ? '; la empresa vinculada queda solo como referencia' : ''}. No cambian la aceptación, el documento, las líneas ni los pagos.`;
  const fields = { 'Quote Recipient': 'person', 'Pipeline Facts Note': [line, f['Pipeline Facts Note'] || ''].filter(Boolean).join('\n') };
  if (!current.length) fields['Stakeholder'] = [contact];
  return { fields };
}

// ── «No aceptar» from the client's space (shared/quoteAcceptance.ts › declineQuote) ──
// Quote Status = Lost and one line in the existing history (Pipeline Facts Note) with the date, who, how and the
// optional comment. The published document is never modified. An accepted quote can never be declined this way.
export const DECLINE_MARK = '[respuesta del cliente: no acepta]';
const oneLine = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
export function declineLine({ at, by, via = 'client_space', comment = '' }) {
  const c = oneLine(comment, 500).replace(/"/g, '”');
  return `${DECLINE_MARK} ${String(at).slice(0, 16).replace('T', ' ')} UTC · por ${oneLine(by, 120) || 'la persona'} · vía ${via}${c ? ` · comentario: "${c}"` : ''}`;
}
/** The client's «No aceptar» kept in the history (the latest one), or null. */
export function declineOf(note = '') {
  const line = String(note || '').split('\n').find(l => l.startsWith(DECLINE_MARK));
  if (!line) return null;
  const m = /^\[respuesta del cliente: no acepta\] (\d{4}-\d{2}-\d{2} \d{2}:\d{2}) UTC · por (.*?) · vía ([a-z_]+)(?: · comentario: "(.*)")?$/.exec(line);
  return m ? { at: `${m[1].replace(' ', 'T')}:00.000Z`, by: m[2], via: m[3], comment: m[4] || '' } : { at: '', by: '', via: '', comment: '' };
}
/** Why the client cannot decline now, or null (the same checks as accepting: what she sees, current conditions). */
export function declineProblem(f = {}, snapshot = null, { seenKey = null } = {}) {
  if (isAccepted(f)) return 'already_accepted';
  if (wasAccepted(f)) return 'quote_was_accepted';
  if (name(f['Quote Status']) === 'Lost') return declineOf(f['Pipeline Facts Note']) ? 'already_declined' : 'quote_lost';
  if (isReplaced(f)) return 'quote_replaced';
  if (!snapshot) return 'not_sent';
  if (hasUnpublishedChanges(f, snapshot)) return 'unpublished_changes';
  if (seenKey !== null && seenKey !== recordContentKey(f)) return 'conditions_changed';
  return null;
}

/** The explicit change of «Solo administración» (quote-recipient.mts › adminOnly): the value, a written reason and a trace line. */
export const ADMIN_ONLY_MARK = '[solo administración:';
export function adminOnlyChange(f = {}, { value, reason = '', by = '', now = new Date() } = {}) {
  if (typeof value !== 'boolean') return { error: 'invalid_value' };
  const r = String(reason || '').replace(/\s+/g, ' ').trim().slice(0, 500);
  if (r.length < 10) return { error: 'reason_required' };
  if ((f['Admin Only'] === true) === value) return { error: 'no_change' };
  const stamp = now.toISOString().slice(0, 16).replace('T', ' ');
  const line = `${ADMIN_ONLY_MARK} ${value ? 'sí' : 'no'}] (${stamp} UTC, ${String(by).slice(0, 120)}): ${value ? 'restringida a la administración' : 'deja de estar restringida: vuelven a aplicar las reglas de siempre'}. Motivo: ${r}`;
  return { fields: { 'Admin Only': value, 'Pipeline Facts Note': [line, f['Pipeline Facts Note'] || ''].filter(Boolean).join('\n') } };
}
